export const WORKBENCH_STAGES = ['chapter_text', 'screenplay', 'storyboard', 'panel_image', 'audio', 'video'] as const

export type WorkbenchStage = (typeof WORKBENCH_STAGES)[number]

export type ParsedWorkbenchConfig =
  | { ok: true; value: unknown }
  | { ok: false; message: string }

export type PanelImageSeedStrategy = 'deterministic' | 'fixed'

export interface StyleLockWorkbenchConfig {
  artStyle: string
  palette: string
  quality: string
  negative: string
}

export interface PanelImageWorkbenchConfig {
  model: string
  size: string
  seedStrategy: PanelImageSeedStrategy
  seed: string
  stylePreset: string
  styleLock: StyleLockWorkbenchConfig
}

export interface ScreenplayWorkbenchConfig {
  style: string
  length: string
  dialogueDensity: string
  pov: string
}

export interface StoryboardWorkbenchConfig {
  shotDensity: string
  preferredShotTypes: string[]
  cameraLanguage: string
  styleLock: string
  sketchReviewEnabled: boolean
}

export interface PanelImageRenderRequest {
  model?: string
  size?: string
  seed?: number
  style_preset?: string
}

export function isWorkbenchStage(value: string | undefined): value is WorkbenchStage {
  return Boolean(value && (WORKBENCH_STAGES as readonly string[]).includes(value))
}

export function parseEditorConfig(text: string): ParsedWorkbenchConfig {
  try {
    return { ok: true, value: JSON.parse(text || '{}') }
  } catch (error) {
    return {
      ok: false,
      message: error instanceof Error ? error.message : 'Invalid JSON',
    }
  }
}

export function formatConfigForEditor(value: unknown): string {
  return JSON.stringify(value ?? {}, null, 2)
}

export function panelImageConfigFromUnknown(value: unknown): PanelImageWorkbenchConfig {
  const record = value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
  const rawSeed = record.seed
  const seed = typeof rawSeed === 'number' && Number.isFinite(rawSeed) ? String(Math.trunc(rawSeed)) : ''
  const configuredStrategy = record.seed_strategy === 'fixed' || record.seed_strategy === 'deterministic'
    ? record.seed_strategy
    : undefined
  return {
    model: stringValue(record.model),
    size: stringValue(record.size),
    seedStrategy: configuredStrategy ?? (seed ? 'fixed' : 'deterministic'),
    seed,
    stylePreset: stringValue(record.style_preset),
    styleLock: styleLockFromUnknown(record.style_lock),
  }
}

export function panelImageConfigToJSON(config: PanelImageWorkbenchConfig): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (config.model.trim()) out.model = config.model.trim()
  if (config.size.trim()) out.size = config.size.trim()
  if (config.stylePreset.trim()) out.style_preset = config.stylePreset.trim()
  const styleLock = styleLockToJSON(config.styleLock)
  if (Object.keys(styleLock).length > 0) out.style_lock = styleLock
  if (config.seedStrategy === 'fixed') {
    out.seed_strategy = 'fixed'
    const seed = Number(config.seed)
    if (Number.isSafeInteger(seed)) out.seed = seed
  } else {
    out.seed_strategy = 'deterministic'
  }
  return out
}

export function screenplayConfigFromUnknown(value: unknown): ScreenplayWorkbenchConfig {
  const record = recordValue(value)
  return {
    style: stringValue(record.style),
    length: stringValue(record.length),
    dialogueDensity: stringValue(record.dialogue_density),
    pov: stringValue(record.pov),
  }
}

export function screenplayConfigToJSON(config: ScreenplayWorkbenchConfig): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (config.style.trim()) out.style = config.style.trim()
  if (config.length.trim()) out.length = config.length.trim()
  if (config.dialogueDensity.trim()) out.dialogue_density = config.dialogueDensity.trim()
  if (config.pov.trim()) out.pov = config.pov.trim()
  return out
}

export function storyboardConfigFromUnknown(value: unknown): StoryboardWorkbenchConfig {
  const record = recordValue(value)
  return {
    shotDensity: stringValue(record.shot_density),
    preferredShotTypes: stringArrayValue(record.preferred_shot_types),
    cameraLanguage: stringValue(record.camera_language),
    styleLock: stringValue(record.style_lock),
    sketchReviewEnabled: record.sketch_review_enabled === true,
  }
}

export function storyboardConfigToJSON(config: StoryboardWorkbenchConfig): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (config.shotDensity.trim()) out.shot_density = config.shotDensity.trim()
  const shotTypes = config.preferredShotTypes.map((value) => value.trim()).filter(Boolean)
  if (shotTypes.length > 0) out.preferred_shot_types = shotTypes
  if (config.cameraLanguage.trim()) out.camera_language = config.cameraLanguage.trim()
  if (config.styleLock.trim()) out.style_lock = config.styleLock.trim()
  if (config.sketchReviewEnabled) out.sketch_review_enabled = true
  return out
}

export function panelImageRenderRequestFromConfig(value: unknown): PanelImageRenderRequest | undefined {
  const config = panelImageConfigFromUnknown(value)
  const request: PanelImageRenderRequest = {}
  if (config.model.trim()) request.model = config.model.trim()
  if (config.size.trim()) request.size = config.size.trim()
  if (config.stylePreset.trim()) request.style_preset = config.stylePreset.trim()
  if (config.seedStrategy === 'fixed') {
    const seed = Number(config.seed)
    if (Number.isSafeInteger(seed)) request.seed = seed
  }
  return Object.keys(request).length > 0 ? request : undefined
}

function styleLockFromUnknown(value: unknown): StyleLockWorkbenchConfig {
  const record = recordValue(value)
  return {
    artStyle: stringValue(record.art_style),
    palette: stringValue(record.palette),
    quality: stringValue(record.quality),
    negative: stringValue(record.negative),
  }
}

function styleLockToJSON(config: StyleLockWorkbenchConfig): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (config.artStyle.trim()) out.art_style = config.artStyle.trim()
  if (config.palette.trim()) out.palette = config.palette.trim()
  if (config.quality.trim()) out.quality = config.quality.trim()
  if (config.negative.trim()) out.negative = config.negative.trim()
  return out
}

function recordValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function stringArrayValue(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}
